import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {
    Typography,
    Button,
    Modal,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    InputAdornment,
    Avatar,
    Chip,
    IconButton,
} from '@mui/material'
import {Search, Delete, Close} from '@mui/icons-material'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {clientApi} from '../../../../services'
import {getInitials, getColorFromName, parseError} from '../../../../utils/helpers'
import {useParams} from 'react-router-dom'
import {useMobile} from '../../../../hooks'

export default ({open, setOpen, loadData, client, allCaregivers, clientCaregivers}) => {
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const {clientId} = useParams()
    const {mobile} = useMobile()
    const [caregivers, setCaregivers] = useState([])
    const [services, setServices] = useState([])
    const [availableCaregivers, setAvailableCaregivers] = useState([])
    const [selectedCaregiver, setSelectedCaregiver] = useState(null)
    const [searchCaregiverTerm, setSearchCaregiverTerm] = useState('')
    const filteredCaregivers = caregivers?.filter(item => item?.display_name?.toLowerCase().includes(searchCaregiverTerm?.toLowerCase()))
    const [selectedServices, setSelectedServices] = useState([])
    const [searchServiceTerm, setSearchServiceTerm] = useState('')
    const filteredServices = services?.filter(item => item?.display_name?.toLowerCase().includes(searchServiceTerm?.toLowerCase()))
    const [assignedCaregivers, setAssignedCaregivers] = useState([])

    useEffect(() => {
        setSelectedCaregiver(null)
        setServices([])
        setSelectedServices([])
        setAssignedCaregivers([])
        setAvailableCaregivers(allCaregivers?.filter(item => !(clientCaregivers ?? []).some(item2 => item2.caregiver_id === item.id)))
    }, [open])

    useEffect(() => {
        setCaregivers(availableCaregivers?.map(item => ({...item, display_name: item?.first_name + ' ' + item?.last_name})))
    }, [availableCaregivers])

    const caregiverChange = event => {
        setSelectedCaregiver(event.target.value)
        setSelectedServices([])
        const caregiver = availableCaregivers?.find(item => item?.id == event.target.value)
        if (caregiver?.services) {
            const availableServices = caregiver?.services?.filter(item =>
                (client?.services ?? []).some(item2 => item2.service_id === item.service_id),
            )
            setServices(availableServices?.map(item => ({...item, id: item?.service_id, display_name: item?.service_name})))
        }
    }

    const renderCaregiverLabel = selected => {
        const caregiver = caregivers.find(item => item.id === selected)
        return caregiver ? caregiver.display_name : 'Select a caregiver'
    }

    const serviceChange = event => {
        const value = event.target.value
        if (value.includes('all')) {
            if (selectedServices.length == services.length) {
                setSelectedServices([])
            } else {
                setSelectedServices(services?.map(item => item.id))
            }
        } else {
            setSelectedServices(value)
        }
    }

    const renderServiceLabel = selecteds => {
        if (selecteds.length == 0) {
            return 'Select services'
        }
        if (services.length == selecteds.length) {
            return 'All Services'
        }
        return services
            .filter(item => selecteds.includes(item.id))
            .map(item => item.display_name)
            .join(', ')
    }

    const submit = async () => {
        if (assignedCaregivers?.length === 0) {
            setMessageBox({
                status: 'warning',
                title: 'No Client Selected!',
                description: 'Please select a client.',
            })
            return
        }

        try {
            setLoading(true)
            for (const assignedClient of assignedCaregivers) {
                await clientApi.createClientCaregivers(branchId, clientId, assignedClient?.value)
            }
            setLoading(false)
            setOpen(false)
            setMessageBox({
                status: 'success',
                title: 'Client Assigned!',
                description: `${client?.first_name} has been assigned to new clients.`,
            })
            loadData()
        } catch (error) {
            setLoading(false)
            setOpen(false)
            const {title, description} = parseError(error)
            if (description) {
                setMessageBox({status: 'error', title, description})
            }
        }
    }

    const addItem = () => {
        if (selectedCaregiver?.length === 0) {
            setMessageBox({
                status: 'warning',
                title: 'No Client Selected!',
                description: 'Please select a client.',
            })
            return
        }

        if (selectedServices.length === 0) {
            setMessageBox({
                status: 'warning',
                title: 'No Service Selected!',
                description: 'Please select a service.',
            })
            return
        }

        const temp = []
        for (const selectedService of selectedServices) {
            temp.push(services?.find(item => item?.id == selectedService))
        }

        const newItem = {
            value: {
                caregiver_id: selectedCaregiver,
                service_ids: selectedServices,
            },
            display: {
                caregiver: caregivers?.find(item => item?.id == selectedCaregiver),
                services: temp,
            },
        }

        setAssignedCaregivers(prev => [...prev, {...newItem, id: Date.now()}])

        setSelectedCaregiver(null)
        setServices([])
        setSelectedServices([])
        setAvailableCaregivers(prev => prev.filter(item => item.id !== selectedCaregiver))
    }

    const removeItem = item => {
        setAssignedCaregivers(prev => prev.filter(item2 => item2.id !== item?.id))

        setSelectedCaregiver(null)
        setServices([])
        setSelectedServices([])
        setAvailableCaregivers(prev => [...prev, item?.display?.client])
    }

    const reset = () => {
        setSelectedCaregiver(null)
        setServices([])
        setSelectedServices([])
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    Assign Caregiver
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                    Cancel
                                </Button>
                                <Button variant="contained" fullWidth onClick={submit}>
                                    Assign Caregiver
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            Assign Caregiver
                        </Typography>
                    )}
                    <br />
                    <div className="select-card">
                        <Typography variant="inputLabel" color="text.secondary">
                            Caregiver
                        </Typography>
                        <FormControl fullWidth style={{marginBottom: 6}}>
                            <Select
                                value={selectedCaregiver}
                                onChange={caregiverChange}
                                renderValue={renderCaregiverLabel}
                                displayEmpty
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                            overflow: 'auto',
                                        },
                                    },
                                }}>
                                <MenuItem disableRipple disableTouchRipple>
                                    <TextField
                                        placeholder="Search caregiver name..."
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={searchCaregiverTerm}
                                        onChange={e => setSearchCaregiverTerm(e.target.value)}
                                        onClick={e => e.stopPropagation()}
                                        onKeyDown={e => e.stopPropagation()}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </MenuItem>
                                {filteredCaregivers.map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <div className="people-card">
                                            {item?.avatar_url ? (
                                                <Avatar src={item?.avatar_url} style={{width: 32, height: 32}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(item?.first_name + ' ' + item?.last_name),
                                                        width: 32,
                                                        height: 32,
                                                        fontSize: 16,
                                                    }}>
                                                    {getInitials(item?.first_name + ' ' + item?.last_name)}
                                                </Avatar>
                                            )}
                                            <Typography variant="body2" color="text.primary">
                                                {item?.first_name + ' ' + item?.last_name}
                                            </Typography>
                                        </div>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <br />
                        <Typography variant="inputLabel" color="text.secondary">
                            Service
                        </Typography>
                        <FormControl fullWidth>
                            <Select
                                multiple
                                value={selectedServices}
                                onChange={serviceChange}
                                renderValue={renderServiceLabel}
                                displayEmpty
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                            overflow: 'auto',
                                        },
                                    },
                                }}>
                                <MenuItem disableRipple disableTouchRipple>
                                    <TextField
                                        placeholder="Search service name..."
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={searchServiceTerm}
                                        onChange={e => setSearchServiceTerm(e.target.value)}
                                        onClick={e => e.stopPropagation()}
                                        onKeyDown={e => e.stopPropagation()}
                                        slotProps={{
                                            input: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Search />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </MenuItem>
                                {services?.length > 0 && (
                                    <MenuItem value="all">
                                        <div className="item">
                                            <Checkbox
                                                checked={selectedServices.length === services.length}
                                                indeterminate={selectedServices.length > 0 && selectedServices.length < services.length}
                                            />
                                            <ListItemText primary="Select All" />
                                        </div>
                                    </MenuItem>
                                )}
                                {filteredServices.map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <div className="item">
                                            <Checkbox checked={selectedServices.includes(item.id)} />
                                            <b>
                                                <Chip
                                                    label={item?.billing_unit?.replace('_', ' ')?.toUpperCase()}
                                                    color="action.selected"
                                                    variant="filled"
                                                    size="small"
                                                    style={{fontWeight: 'bold', backgroundColor: '#0000000A'}}
                                                />
                                            </b>
                                            <ListItemText primary={item.display_name} />
                                        </div>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <br />
                        <div className="add-actions">
                            <Button variant="outlined" size="small" style={{width: 92}} onClick={reset}>
                                Reset
                            </Button>
                            <Button variant="contained" size="small" style={{width: 92}} onClick={addItem}>
                                Save
                            </Button>
                        </div>
                    </div>
                    {assignedCaregivers?.map((item, index) => (
                        <div key={index}>
                            <br />
                            <div className="assigned-card">
                                <div className="assigned-top">
                                    <div className="people-card">
                                        {item?.display?.caregiver?.avatar_url ? (
                                            <Avatar src={item?.display?.caregiver?.avatar_url} style={{width: 40, height: 40}} />
                                        ) : (
                                            <Avatar
                                                style={{
                                                    backgroundColor: getColorFromName(
                                                        item?.display?.caregiver?.first_name + ' ' + item?.display?.caregiver?.last_name,
                                                    ),
                                                    width: 40,
                                                    height: 40,
                                                    fontSize: 20,
                                                }}>
                                                {getInitials(item?.display?.caregiver?.first_name + ' ' + item?.display?.caregiver?.last_name)}
                                            </Avatar>
                                        )}
                                        <Typography variant="h7" color="text.primary">
                                            {item?.display?.caregiver?.first_name + ' ' + item?.display?.caregiver?.last_name}
                                        </Typography>
                                    </div>
                                    <IconButton onClick={() => removeItem(item)}>
                                        <Delete color="error" />
                                    </IconButton>
                                </div>
                                <div className="assigned-content">
                                    <Typography variant="h9" color="text.secondary" style={{minWidth: 120, paddingTop: 6}}>
                                        Assigned Service:
                                    </Typography>
                                    <div className="services-content">
                                        {item?.display?.services?.map((item2, index2) => (
                                            <Chip label={item2?.service_name} color="action.selected" variant="filled" key={index2} />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" fullWidth onClick={submit}>
                                Assign Caregiver
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
